import Layout from '@/components/Layout';
import ImagePreviewCarousel, {
  ImagePreviewSlide,
} from '@/components/ImagePreviewCarousel';
import TourTypeContentPage from '@/components/TourTypeContent';
import {
  getMetaDataPage,
  getTourTypeContent,
  PageMetaOutput,
} from '@/services/directus';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import Button from '@mui/material/Button';
import GlobalStyles from '@mui/material/GlobalStyles';
import Typography from '@mui/material/Typography';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ReactElement, useMemo } from 'react';
import { getImageUrl } from '@/utils/utils';
import { useRouter } from 'next/router';
import {
  comboBookNowButtonSx,
  comboBookNowWrapSx,
  comboCardContentSx,
  comboCardSx,
  comboCardTitleRowSx,
  comboCardTitleSx,
  comboSectionSx,
  hoianToursRichContentGlobalStyles,
  showcaseCardSx,
  showcaseDescriptionSx,
  showcaseGridSx,
  showcaseItemSx,
  showcaseSectionSx,
  showcaseTitleSx,
} from '@/styles/hoiantours-page-style';
import Image from 'next/image';
import Head from 'next/head';
import { Trans } from 'react-i18next';

type HoianToursPageProps = {
  content: any;
  leftContentHtml: string;
  rightContentHtml: string;
  metadata: PageMetaOutput;
};

type FavoriteCombo = {
  title: string;
  content: string;
};

const buildShowcaseSlidesFromUrls = (
  imageUrls: string[],
  fallbackAlt: string,
): ImagePreviewSlide[] => {
  const uniqueImages = new Set<string>();
  const slides = imageUrls
    .map(imageUrl => imageUrl?.trim())
    .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
    .filter(imageUrl => {
      if (uniqueImages.has(imageUrl)) return false;
      uniqueImages.add(imageUrl);
      return true;
    })
    .slice(0, 8)
    .map(imageUrl => ({
      src: imageUrl,
      alt: fallbackAlt,
    }));

  if (slides.length > 0) return slides;

  return [{ src: '/assets/images/logo.png', alt: fallbackAlt }];
};

const HoianToursPage = ({
  content,
  leftContentHtml,
  rightContentHtml,
  metadata,
}: HoianToursPageProps) => {
  const router = useRouter();
  const experienceSlides = useMemo(
    () =>
      buildShowcaseSlidesFromUrls(
        Array.isArray(content?.experience_images)
          ? content.experience_images.map((img: any) =>
              getImageUrl(img.directus_files_id),
            )
          : [],
        'Experiences',
      ),
    [content?.experience_images],
  );
  const adventureSlides = useMemo(
    () =>
      buildShowcaseSlidesFromUrls(
        Array.isArray(content?.adventure_images)
          ? content.adventure_images.map((img: any) =>
              getImageUrl(img.directus_files_id),
            )
          : [],
        'Adventures',
      ),
    [content?.adventure_images],
  );
  const imageComboTour = useMemo(
    () =>
      content?.combotour_image ? getImageUrl(content.combotour_image) : '',
    [content?.combotour_image],
  );
  const favoriteCombos = useMemo(
    () => content?.favorite_combos || [],
    [content?.favorite_combos],
  );

  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
      </Head>
      <GlobalStyles styles={hoianToursRichContentGlobalStyles} />
      <TourTypeContentPage
        content={content}
        leftContentHtml={leftContentHtml}
        rightContentHtml={rightContentHtml}
        sectionBackgroundColor="#012F5B"
      />
      <Box component="section" sx={showcaseSectionSx}>
        <Container maxWidth="lg">
          <Box sx={showcaseGridSx}>
            <Box sx={showcaseItemSx}>
              <Typography component="h2" sx={showcaseTitleSx}>
                Experiences
              </Typography>
              <Box sx={showcaseCardSx}>
                <ImagePreviewCarousel
                  slides={experienceSlides}
                  label="Experiences"
                />
              </Box>
              <Box sx={showcaseDescriptionSx}>
                <div
                  className="prose hoiantours-rich-content"
                  dangerouslySetInnerHTML={{
                    __html: content?.experience_content || '',
                  }}
                />
              </Box>
            </Box>

            <Box sx={showcaseItemSx}>
              <Typography component="h2" sx={showcaseTitleSx}>
                Adventures
              </Typography>
              <Box sx={showcaseCardSx}>
                <ImagePreviewCarousel
                  slides={adventureSlides}
                  label="Adventures"
                />
              </Box>
              <Box sx={showcaseDescriptionSx}>
                <div
                  className="prose hoiantours-rich-content"
                  dangerouslySetInnerHTML={{
                    __html: content?.adventure_content || '',
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ ...showcaseGridSx, ...comboSectionSx }}>
            <Box sx={showcaseItemSx}>
              <Typography component="h2" sx={showcaseTitleSx}>
                Combo Tours
              </Typography>
              <Box sx={showcaseCardSx}>
                <Image
                  src={imageComboTour || '/assets/images/logo.png'}
                  alt="Combo Tours"
                  fill
                  sizes="(max-width: 900px) 100vw, 50vw"
                  style={{ objectFit: 'cover' }}
                />
              </Box>
              <Box sx={showcaseDescriptionSx}>
                <div
                  className="prose hoiantours-rich-content"
                  dangerouslySetInnerHTML={{
                    __html: content?.combotour_content || '',
                  }}
                />
              </Box>
            </Box>

            <Box sx={showcaseItemSx}>
              <Typography component="h2" sx={showcaseTitleSx}>
                Favorite Combos
              </Typography>
              <Box>
                {favoriteCombos.map((combo: FavoriteCombo, index: number) => (
                  <Box key={`${combo.title}-${index}`} sx={comboCardSx(index)}>
                    <Box sx={comboCardTitleRowSx}>
                      <ExploreOutlinedIcon sx={{ fontSize: 20 }} />
                      <Typography component="h3" sx={comboCardTitleSx}>
                        {combo.title}
                      </Typography>
                    </Box>
                    <Typography sx={comboCardContentSx}>
                      {combo.content}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box sx={comboBookNowWrapSx}>
            <Button
              variant="contained"
              sx={comboBookNowButtonSx}
              onClick={() =>
                router.push({
                  pathname: '/',
                  query: {
                    tab: 'tour',
                  },
                })
              }
            >
              <Trans i18nKey="hoiAntour.bookNow" defaults="Book Now" />
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

HoianToursPage.getLayout = (page: ReactElement) => (
  <Layout showTabs hasFooter hasPadding={false} headerBackgroundColor="#012F5B">
    {page}
  </Layout>
);

HoianToursPage.type = 'public';

export async function getServerSideProps({ locale }: { locale: string }) {
  const lang = locale ?? 'en';
  const pageType = 'hoiantours';
  const i18nProps = await serverSideTranslations(lang, ['common', 'validate', 'aboutUs']);
  const normalizedLang = lang.split('-')[0];
  const localizedFallbackDescription =
    (i18nProps as any)?._nextI18Next?.initialI18nStore?.[lang]?.common?.hoiAntour?.metaDescription ??
    (i18nProps as any)?._nextI18Next?.initialI18nStore?.[normalizedLang]?.common?.hoiAntour?.metaDescription;
  const fallbackTitle = 'HoiAnGO - Tour Options';
  const fallbackDescription =
    typeof localizedFallbackDescription === 'string' && localizedFallbackDescription.trim()
      ? localizedFallbackDescription
      : 'Book a Hoi An Tour with Hoi An GO! Choose from our Adventure & Experience Tours of Hoi An. Travel by private E-Car and see a different side of Hoi An.';
  let metadata: PageMetaOutput = {
    title: fallbackTitle,
    description: fallbackDescription,
  };
  let leftContentHtml = '';
  let rightContentHtml = '';
  let content = null;

  try {
    metadata = await getMetaDataPage({
      type: pageType,
      fallbackTitle,
      fallbackDescription,
    });
  } catch (error) {
    console.error('[hoiantours] failed to load metadata', error);
  }

  try {
    content = await getTourTypeContent({
      lang,
      type: pageType,
      fields: [
        '*',
        {
          translations: [
            '*',
            { experience_images: ['*'], adventure_images: ['*'] },
          ],
        },
      ],
    });
    leftContentHtml = content?.leftContentHtml ?? '';
    rightContentHtml = content?.rightContentHtml ?? '';
  } catch (error) {
    console.error('[hoiantours] failed to load Directus content', error);
  }
  return {
    props: {
      content: content?.translation,
      leftContentHtml,
      rightContentHtml,
      metadata,
      ...i18nProps,
    },
  };
}

export default HoianToursPage;
